/**
 * @file `/` — onboarding, and nothing else.
 *
 * Five states, in order:
 *   pending            → loading
 *   error              → retry panel (never a blank screen)
 *   no portfolio       → create one (a signed-up user with none must be able to)
 *   landlord somewhere → REDIRECT to the first such portfolio's dashboard
 *   resident only      → the calm resident screen (no shell, no 403 storm)
 *
 * ## Why there is no picker any more
 *
 * This screen used to be a mandatory list of workspaces you had to choose from
 * before seeing anything. That was the RLS tenancy key leaking into the UI: the
 * database needs `workspace_id` on every row, but a landlord thinks "my
 * properties", not "my workspace" — and being asked to choose between one option
 * is a question carrying no information.
 *
 * So the common case (one portfolio) never asks. A landlord with several switches
 * from the sidebar, which is where a context switch belongs — persistent and
 * reversible, not a gate on the way in.
 *
 * ## Why the role decides, not the position in the list
 *
 * `GET /me` lists EVERY membership, and a membership's role may be `tenant` or
 * `guarantor` — roles the API refuses on every landlord route (`LANDLORD_ROLES`,
 * shared with `apps/api/src/authz.ts`). Opening the shell on such a workspace would
 * render a page of refusals. So the destination is the first workspace where the
 * person is a landlord, and a person who is only ever a resident gets
 * `NoLandlordAccessScreen` instead of either the shell or the create form.
 *
 * `replace: true` so the back button does not bounce into `/` and straight out.
 */
import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { isLandlordRole } from "@neighboros/shared";
import { useCreateWorkspace, useMe } from "../api/queries";
import { NoLandlordAccessScreen } from "./NoLandlordAccessScreen";
import { WorkspaceCreateForm } from "./WorkspaceCreateForm";
import { CenteredPanel, ErrorPanel, Spinner } from "./ui";

/**
 * The signed-in landing outside any workspace. It is the page's only content, so it is the <main>:
 * without one the loading, error and create-workspace states sit outside every landmark.
 */
export function WorkspaceGate() {
  return (
    <main className="flex-1">
      <WorkspaceGateContent />
    </main>
  );
}

function WorkspaceGateContent() {
  const me = useMe();
  const createWorkspace = useCreateWorkspace();
  const navigate = useNavigate();

  const workspaces = me.status === "success" ? me.data.workspaces : undefined;
  const landlordWorkspaceId = workspaces?.find((workspace) => isLandlordRole(workspace.role))?.id;
  // Workspaces exist, and none of them is one this person may run.
  const residentOnly = workspaces !== undefined && workspaces.length > 0 && landlordWorkspaceId === undefined;

  useEffect(() => {
    if (landlordWorkspaceId === undefined) return;
    void navigate({
      to: "/workspaces/$workspaceId",
      params: { workspaceId: landlordWorkspaceId },
      replace: true,
    });
  }, [landlordWorkspaceId, navigate]);

  if (me.status === "pending") {
    return (
      <CenteredPanel>
        <Spinner />
        Loading your portfolio…
      </CenteredPanel>
    );
  }

  if (me.status === "error") {
    return (
      <ErrorPanel
        error={me.error}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (landlordWorkspaceId !== undefined) {
    // The effect above is navigating; render the same spinner rather than a flash
    // of the create form.
    return (
      <CenteredPanel>
        <Spinner />
        Opening your portfolio…
      </CenteredPanel>
    );
  }

  if (residentOnly) {
    return <NoLandlordAccessScreen />;
  }

  return (
    <div className="mx-auto w-full max-w-xl">
      <WorkspaceCreateForm
        isSubmitting={createWorkspace.isPending}
        error={createWorkspace.error}
        onCreate={(name) => {
          createWorkspace.mutate(name, {
            onSuccess: (workspace) => {
              void navigate({
                to: "/workspaces/$workspaceId",
                params: { workspaceId: workspace.id },
                replace: true,
              });
            },
          });
        }}
      />
    </div>
  );
}
